import { useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";

import classes from "~/pages/BearingFrequencyCalculatorPage/BearingFrequencyCalculatorPage.module.less";
import { type BearingFrequencyCalculationResult } from "~/pages/BearingFrequencyCalculatorPage/utils/calculateBearingFrequency";
import { downloadBearingFrequencyPdf } from "~/pages/BearingFrequencyCalculatorPage/api/downloadBearingFrequencyPdf";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { DownloadClickLink } from "~/common/components/Link";
import { FormBFC } from "~/pages/BearingFrequencyCalculatorPage/components/FormBFC";
import { TableBFC } from "~/pages/BearingFrequencyCalculatorPage/components/TableBFC";
import { trackBearing } from "~/app/analytics/ga/ga";
import { useTranslation } from "react-i18next";

export interface BearingFrequencyCalculatorForm {
  isInch: boolean;
  pitchDiameter: number | null;
  rollingDiameter: number | null;
  isDeg: boolean;
  contactAngle: number | null;
  rollingElementAmount: number | null;
  speed: number | null;
}

const RESULT_NF = NumberFormatter.getFormatter({
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const INPUT_NF = NumberFormatter.getFormatter();

export function BearingFrequencyCalculator() {
  const { t } = useTranslation();
  const [result, setResult] = useState<BearingFrequencyCalculationResult | null>(null);

  function handleChange(newValue: BearingFrequencyCalculationResult | null) {
    setResult(newValue);
  }

  return (
    <div className={classes.container}>
      <div className={classes.pageTitle}>
        <PageTitle title={t("bearingFrequencyCalculator.title")} />
      </div>

      <FormBFC handleChange={handleChange} />

      <div
        className={classes.infoText}
        dangerouslySetInnerHTML={{ __html: t("bearingFrequencyCalculator.infoText") }}
      />

      <DownloadPDF result={result} />
      <div className={classes.tableResult}>
        <TableBFC result={result} />
      </div>
    </div>
  );
}

interface DownloadProps {
  result: BearingFrequencyCalculationResult | null;
}

const DownloadPDF = ({ result }: DownloadProps) => {
  const isValidResult = !!result;

  const downloadPdf = () => {
    if (!result) {
      return;
    }
    const {
      cor,
      or,
      re,
      cir,
      ir,
      isInch,
      isDeg,
      rollingDiameter,
      pitchDiameter,
      rollingElementAmount,
      speed,
      contactAngle,
    } = result;
    trackBearing("click_on_download_results_table");
    downloadBearingFrequencyPdf({
      isInch,
      isDeg,
      rollingDiameter: INPUT_NF.format(rollingDiameter),
      pitchDiameter: INPUT_NF.format(pitchDiameter),
      rollingElementAmount: INPUT_NF.format(rollingElementAmount),
      speed: INPUT_NF.format(speed),
      contactAngle: INPUT_NF.format(contactAngle),
      cor: RESULT_NF.format(cor),
      or: RESULT_NF.format(or),
      re: RESULT_NF.format(re),
      cir: RESULT_NF.format(cir),
      ir: RESULT_NF.format(ir),
    });
  };

  const { t } = useTranslation();
  return (
    <div className={classes.downloadLink}>
      <span className="scha__order-list__download-title mr-16">{t("page.bearingFrequencyCalculator.download")}</span>
      <DownloadClickLink onClick={downloadPdf} disabled={!isValidResult}>
        {t("downloadFile.type.pdf")}
      </DownloadClickLink>
    </div>
  );
};
